/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { BEM } from "@adapptio/lib-components";
import { ReadmeEditorView } from "../ReadmeEditorView";
import { useStateWithEvent } from "../../../../ui/hooks";
import { isSelectionItemPage } from "../../Common/ReadmeEditSelection";
import { TGetBlueprintSchemaModel } from "@adapptio/lib-blueprint";
import { TReadmePageSchema } from "@adapptio/lib-core";
import { Panel, PANEL_STYLE, PanelNote } from "../../../../ui/components/Panel";
import { ReadmeLink } from "./ReadmeLink";

export interface IReadmeViewModeProps {
	view: ReadmeEditorView;
}

export const ReadmeViewMode: FC<IReadmeViewModeProps> = ({ view }) => {
	const baseClass = BEM("edt-readme-view-mode");
	const model = view.getModel();
	const selection = useMemo(() => model.getSelection(), [ model ]);

	const getSelectedPage = (): TGetBlueprintSchemaModel<TReadmePageSchema> | null => {
		const items = selection.getItems();
		if (items.length === 0) {
			return null;
		}
		const selectedItem = items[0];
		if (!isSelectionItemPage(selectedItem)) {
			return null;
		}
		const pagesModel = model.getModel()?.props.spec.props.pages;
		if (!pagesModel) {
			return null;
		}
		return pagesModel.items.find((item) => item.nodeId === selectedItem.nodeId) || null;
	};

	const selectedPage = useStateWithEvent(
		selection.onChange,
		() => getSelectedPage(),
		[ model, selection ]
	);

	// Also update when model changes
	useStateWithEvent(
		model.onModelChange,
		() => getSelectedPage(),
		[ model, selection ]
	);

	// Custom components for ReactMarkdown
	const markdownComponents = useMemo(
		() => ({
			a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
				<ReadmeLink {...props} view={view} />
			)
		}),
		[ view ]
	);

	if (!selectedPage) {
		return (
			<Panel style={PANEL_STYLE.NONE} className={baseClass.toClassName()}>
				<PanelNote align="center">
					No page selected. Add a page using the + button in the sidebar.
				</PanelNote>
			</Panel>
		);
	}

	const content = selectedPage.props.content.value || "";

	return (
		<Panel style={PANEL_STYLE.NONE} scroll className={baseClass.toClassName()}>
			<ReactMarkdown
				className={baseClass.element("content").add("edt-markdown-content").toClassName()}
				components={markdownComponents}
			>
				{content}
			</ReactMarkdown>
		</Panel>
	);
};
